import React from "react";

import { noop } from "lodash";

import AppContext from "~/common/AppContext";
import DesktopAnalytics from "~/lib/desktopApi/analytics";
import DesktopGraphQL from "~/lib/desktopApi/graphql";
import { notifyDesktopAppOfAuth } from "~/lib/desktopApi/userAuthStatus";
import { isCtrlOrCmd } from "~/lib/utils";

/**
 * Transform current URLs to companion mode URLs
 */
export function transformUrlToCompanionMode(url: string): string {
	try {
		const urlObj = new URL(url, window.location.origin);
		const pathname = urlObj.pathname;
		const search = urlObj.search;

		// Transform /meetings/:eventGuid/ to /companion-mode/event/:eventGuid/
		const meetingMatch = pathname.match(/^\/meetings\/([^/]+)\/?(.*)$/);
		if (meetingMatch) {
			const eventGuid = meetingMatch[1];
			const rest = meetingMatch[2];
			return `/companion-mode/event/${eventGuid}/${rest ? rest : ""}${search}`;
		}

		// TODO: transform stream URLs as well.

		// Return original URL if no transformation needed
		return url;
	} catch (error) {
		// Return original URL if parsing fails
		return url;
	}
}

export default function DesktopApi() {
	const { openSettingsModal } = React.useContext(AppContext);
	React.useEffect(() => {
		notifyDesktopAppOfAuth();
	}, []);

	React.useEffect(() => {
		if (!(window.electron?.companionModeCanNavigate && window.electron?.navigateCompanionMode)) {
			return noop;
		}

		function openLinkInCompanionMode(e: MouseEvent) {
			if (!(window.electron?.companionModeCanNavigate && window.electron?.navigateCompanionMode)) {
				return;
			}
			const { target } = e;
			if (!(target instanceof HTMLElement)) return;
			const link = target.closest("a");

			// note: this relies on the fact that react-router doesn't preventDefault on link clicks when modifier keys are held down
			if (link && !e.defaultPrevented && isCtrlOrCmd(e) && window.electron.companionModeCanNavigate(link.href)) {
				window.electron.navigateCompanionMode(transformUrlToCompanionMode(link.href));
				e.preventDefault();
			}
		}

		document.addEventListener("click", openLinkInCompanionMode);
		return () => document.removeEventListener("click", openLinkInCompanionMode);
	}, []);

	React.useEffect(() => {
		window.electron?.registerDesktopSettingsOpener?.(() => {
			openSettingsModal("preferences");
		});
		window.electron?.registerNotificationSettingsOpener?.(() => {
			openSettingsModal("notifications");
		});

		return () => {
			window.electron?.registerDesktopSettingsOpener?.(undefined);
			window.electron?.registerNotificationSettingsOpener?.(undefined);
		};
	}, [openSettingsModal]);

	return (
		<>
			<DesktopAnalytics />
			<DesktopGraphQL />
		</>
	);
}
